import { useState } from "react";
import { twMerge } from "tailwind-merge";
import {
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  FilePlus2,
  FileText,
  Loader2,
  Pencil,
  Search,
  Trash2,
  Wrench,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useToolLabels } from "./ToolLabelsContext";
import { labelForTool } from "./ToolCallIndicator";
import { TodoList, type TodoItem, type TodoDelegation } from "./TodoList";
import {
  categoryForTool,
  type ToolActivityGroup,
  type ToolActivityItem,
  type ToolCategoryName,
} from "../hooks/toolActivity";

// ─────────────────────────────────────────────────────────────────────────
// Category display metadata
// ─────────────────────────────────────────────────────────────────────────

interface CategoryMeta {
  /** Tally chip label — past tense ("Read 3 · Edited 2 · Ran 1"). */
  tally: string;
  /** Row verb while the call is in flight. */
  running: string;
  /** Row verb once the result lands. */
  done: string;
  icon: LucideIcon;
}

const CATEGORY_META: Record<ToolCategoryName, CategoryMeta> = {
  read: { tally: "Read", running: "Reading", done: "Read", icon: FileText },
  search: { tally: "Searched", running: "Searching", done: "Searched", icon: Search },
  edit: { tally: "Edited", running: "Editing", done: "Edited", icon: Pencil },
  create: { tally: "Created", running: "Creating", done: "Created", icon: FilePlus2 },
  remove: { tally: "Removed", running: "Removing", done: "Removed", icon: Trash2 },
  delegate: { tally: "Delegated", running: "Delegating", done: "Delegated", icon: Bot },
  work: { tally: "Ran", running: "Working on", done: "Ran", icon: Wrench },
};

/** Tally order matches the mockup's reading order: reads before writes. */
const CATEGORY_ORDER: ToolCategoryName[] = [
  "read",
  "search",
  "edit",
  "create",
  "remove",
  "delegate",
  "work",
];

// ─────────────────────────────────────────────────────────────────────────
// Row labeling
// ─────────────────────────────────────────────────────────────────────────

/**
 * The thing a row's verb acts on, pulled from the tool's args: a file
 * path, a search pattern/query, or a task description. `mono` renders
 * the subject in monospace (paths, patterns).
 */
// The website coding agent's FS is slug-native: every path is
// `{slug}/…`, where `{slug}` is the per-site working directory
// (`rapid-plumbing-1/src/components/Hero.tsx`). That prefix is noise in
// the transcript — the user already knows which site they're on — so we
// drop the leading segment to read `src/components/Hero.tsx`. Guarded by
// a known-roots set so an already-relative path (or the meaningful
// `workspace/` business namespace) is never over-stripped.
const KNOWN_PATH_ROOTS = new Set(["src", "public", "workspace", "node_modules"]);

function stripProjectPrefix(path: string): string {
  const clean = path.replace(/^\/+/, "");
  const slash = clean.indexOf("/");
  if (slash === -1) return clean;
  if (KNOWN_PATH_ROOTS.has(clean.slice(0, slash))) return clean;
  return clean.slice(slash + 1);
}

function subjectForArgs(
  args: Record<string, unknown> | undefined
): { text: string; mono: boolean } | null {
  if (!args) return null;
  const filePath = args.file_path ?? args.path;
  if (typeof filePath === "string" && filePath.trim()) {
    return { text: stripProjectPrefix(filePath), mono: true };
  }
  if (typeof args.pattern === "string" && args.pattern.trim()) {
    return { text: args.pattern, mono: true };
  }
  const q = args.q as Record<string, unknown> | undefined;
  const query = typeof args.query === "string" ? args.query : q?.query;
  if (typeof query === "string" && query.trim()) {
    const truncated = query.length > 40 ? `${query.slice(0, 40)}…` : query;
    return { text: `"${truncated}"`, mono: false };
  }
  if (typeof args.description === "string" && args.description.trim()) {
    const d = args.description;
    return { text: d.length > 60 ? `${d.slice(0, 60)}…` : d, mono: false };
  }
  return null;
}

function humanize(identifier: string): string {
  return identifier
    .replace(/[_.]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const MCP_UNIVERSAL = new Set(["search", "call", "describe", "list_resources"]);

// ─────────────────────────────────────────────────────────────────────────
// Rows
// ─────────────────────────────────────────────────────────────────────────

/**
 * "Site builder: Update the navigation…" — who a `delegate` row went to,
 * plus what they were asked to do. Falls back to the bare verb while
 * args stream in.
 */
function delegateSentence(
  args: Record<string, unknown> | undefined,
  agents: Record<string, string> | undefined
): string | null {
  const type =
    typeof args?.subagent_type === "string" && args.subagent_type.trim()
      ? args.subagent_type
      : undefined;
  const description =
    typeof args?.description === "string" && args.description.trim()
      ? args.description
      : undefined;
  if (!type && !description) return null;

  const truncated =
    description && description.length > 80
      ? `${description.slice(0, 80)}…`
      : description;
  // `description` streams before `subagent_type` (schema order), so a
  // mid-stream row shows the task alone until the agent name arrives.
  if (!type) return truncated ?? null;
  const agent = agents?.[type] ?? humanize(type);
  return truncated ? `${agent}: ${truncated}` : agent;
}

function ActivityRow({
  item,
  category,
}: {
  item: ToolActivityItem;
  category: ToolCategoryName;
}) {
  const labels = useToolLabels();
  const meta = CATEGORY_META[category];
  const running = item.status === "running";

  // Label priority:
  //   1. MCP universal tools — the arg-driven sentence labels
  //      ("Searching stock photos for \"dome\"") already read better
  //      than any verb+subject we could compose.
  //   2. Delegate rows — who it went to + what they were asked
  //      ("Site builder: Update the navigation…").
  //   3. Category verb + subject ("Edited src/pages/index.astro") when
  //      the args carry something concrete.
  //   4. Consumer's static label ("Saving your answers").
  //   5. Humanized tool name — the visible-by-default fallback.
  let verb: string | null = null;
  let subject: { text: string; mono: boolean } | null = null;
  let sentence: string | null = null;

  if (MCP_UNIVERSAL.has(item.name)) {
    sentence = labelForTool(item.name, item.args, labels, running ? "present" : "past");
  }
  if (!sentence && category === "delegate") {
    sentence = delegateSentence(item.args, labels.agents);
    // Args not streamed yet — say what's happening rather than "Task".
    if (!sentence) sentence = running ? meta.running : meta.done;
  }
  if (!sentence) {
    subject = subjectForArgs(item.args);
    if (subject) {
      verb = running ? meta.running : meta.done;
    } else {
      sentence = labels.tools?.[item.name] ?? humanize(item.name);
    }
  }

  return (
    <div
      className={twMerge(
        "flex items-center gap-2 py-1",
        running && "text-base-400 italic"
      )}
      data-testid="tool-activity-row"
    >
      {/* No per-row spinner: a burst can hold 10+ in-flight rows, and a
          page of independently-phased rotations reads as chaos (and costs
          real main-thread time). The card header carries the single
          spinner; a running row is marked by tint + italics + ellipsis. */}
      <meta.icon
        className={twMerge(
          "h-3.5 w-3.5 shrink-0",
          running ? "text-primary-500" : "text-base-400"
        )}
      />
      <span className="min-w-0 flex-1 truncate">
        {sentence ?? (
          <>
            {verb}{" "}
            <span className={subject?.mono ? "font-mono" : undefined}>
              {subject?.text}
            </span>
          </>
        )}
        {running && "…"}
      </span>
      {item.status === "complete" && (
        <Check className="h-3.5 w-3.5 shrink-0 text-green-600" aria-label="done" />
      )}
      {item.status === "error" && (
        <X className="h-3.5 w-3.5 shrink-0 text-red-500" aria-label="failed" />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tallies
// ─────────────────────────────────────────────────────────────────────────

function tallyCounts(
  items: ToolActivityItem[],
  categories: Record<string, ToolCategoryName> | undefined
): Array<{ category: ToolCategoryName; count: number }> {
  const counts = new Map<ToolCategoryName, number>();
  for (const item of items) {
    const category = categoryForTool(item.name, categories);
    counts.set(category, (counts.get(category) ?? 0) + 1);
  }
  return CATEGORY_ORDER.filter((c) => counts.has(c)).map((category) => ({
    category,
    count: counts.get(category)!,
  }));
}

function TallyChips({
  items,
  withLabels,
}: {
  items: ToolActivityItem[];
  withLabels: boolean;
}) {
  const { categories } = useToolLabels();
  const tallies = tallyCounts(items, categories);

  return (
    <span className="flex items-center gap-3" data-testid="tool-activity-tallies">
      {tallies.map(({ category, count }) => {
        const meta = CATEGORY_META[category];
        return (
          <span
            key={category}
            className="flex items-center gap-1"
            aria-label={`${meta.tally} ${count}`}
          >
            <meta.icon className="h-3.5 w-3.5 text-base-400" />
            {withLabels && <span className="text-base-500">{meta.tally}</span>}
            <span className="font-medium text-base-600">{count}</span>
          </span>
        );
      })}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Shared feed pieces
// ─────────────────────────────────────────────────────────────────────────

/**
 * A dead run's unresolved calls should render settled-but-unchecked
 * rather than spinning forever.
 */
function settleForDeadRun(
  items: ToolActivityItem[],
  isStreaming: boolean
): ToolActivityItem[] {
  if (isStreaming) return items;
  return items.map((item) =>
    item.status === "running" ? { ...item, status: "complete" as const } : item
  );
}

function FeedRows({ items }: { items: ToolActivityItem[] }) {
  const { categories } = useToolLabels();
  return (
    <div className="border-l border-base-200 pl-2">
      {items.map((item) => (
        <ActivityRow
          key={item.id}
          item={item}
          category={categoryForTool(item.name, categories)}
        />
      ))}
    </div>
  );
}

/** Spinner + "Working on it…" + icon-only tallies — the live header. */
function WorkingHeader({ items }: { items: ToolActivityItem[] }) {
  return (
    <div className="flex items-center justify-between gap-2 pb-1">
      <span className="flex items-center gap-2 font-medium text-base-600">
        <Loader2 className="h-4 w-4 animate-spin text-primary-500" />
        Working on it…
      </span>
      <TallyChips items={items} withLabels={false} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ToolActivity — one burst, inline in the transcript
// ─────────────────────────────────────────────────────────────────────────

export interface ToolActivityProps {
  group: ToolActivityGroup;
  /**
   * Whether the stream that produced this group is still running.
   * When false, unresolved items stop animating — a dead run's calls
   * shouldn't spin forever.
   */
  isStreaming?: boolean;
  className?: string;
}

/**
 * Renders one tool-activity burst — the C-live design:
 *
 *   - **Live** (any call still running while streaming): a card with a
 *     spinner header ("Working on it…"), ticking tallies, and the
 *     individual calls streaming beneath.
 *   - **Settled**: collapses to a one-line tally strip
 *     ("Read 3 · Edited 2 · Created 1 · details"); "details" expands
 *     the full row breakdown.
 */
export function ToolActivity({ group, isStreaming = false, className }: ToolActivityProps) {
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);

  if (group.items.length === 0) return null;

  const hasRunning = group.items.some((i) => i.status === "running");
  const live = hasRunning && isStreaming;
  // Live groups always show their feed; settled ones default collapsed
  // and expand via the "details" toggle. The user's toggle only applies
  // to the settled state — when a group goes live again (new calls
  // appended mid-stream) the feed comes back.
  const expanded = live || (userExpanded ?? false);
  const displayItems = settleForDeadRun(group.items, isStreaming);

  if (live) {
    return (
      <div
        className={twMerge(
          "rounded-xl border border-base-200 bg-base-50 px-3 py-2 text-sm",
          className
        )}
        role="status"
        aria-label="Working on it"
        data-testid="tool-activity"
        data-state="live"
      >
        <WorkingHeader items={displayItems} />
        <FeedRows items={displayItems} />
      </div>
    );
  }

  return (
    <div
      className={twMerge("text-sm", className)}
      data-testid="tool-activity"
      data-state="settled"
    >
      <button
        type="button"
        onClick={() => setUserExpanded((prev) => !(prev ?? false))}
        className="flex items-center gap-3 rounded-lg border border-base-200 bg-base-50 px-3 py-1.5 text-left hover:bg-base-100"
        aria-expanded={expanded}
        data-testid="tool-activity-summary"
      >
        <TallyChips items={displayItems} withLabels />
        <span className="flex items-center gap-0.5 text-xs text-base-400">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
          details
        </span>
      </button>
      {expanded && <div className="mt-1">{<FeedRows items={displayItems} />}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// AgentActivity — the current burst joined to the todo checklist
// ─────────────────────────────────────────────────────────────────────────

export interface AgentActivityProps {
  /**
   * The current-turn burst, if one is active. Its feed renders above the
   * todos so the "how" sits with the "what". Omit when todos exist but no
   * tool call has fired yet — the card degrades to a bare checklist.
   */
  group?: ToolActivityGroup;
  /** The todo checklist — the plan the feed is satisfying. */
  todos?: TodoItem[];
  /** `task` dispatches, so each todo can name who's working it. */
  delegations?: TodoDelegation[];
  isStreaming?: boolean;
  className?: string;
}

/**
 * The cohesion card: one bordered container holding the live tool-activity
 * feed AND the todo checklist, so they read as a single unit of work
 * ("here's what I'm doing / here's the plan it's satisfying") instead of
 * two disconnected widgets. A surface renders exactly one of these,
 * trailing, for the turn in progress; settled bursts from earlier beats
 * stay inline as <ToolActivity> tally strips.
 *
 * With no feed it's a plain checklist; with no todos it's just the feed —
 * so no-todo surfaces can share the same component.
 */
export function AgentActivity({
  group,
  todos,
  delegations,
  isStreaming = false,
  className,
}: AgentActivityProps) {
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);

  const items = group ? settleForDeadRun(group.items, isStreaming) : [];
  const hasFeed = items.length > 0;
  const live = hasFeed && items.some((i) => i.status === "running") && isStreaming;
  const hasTodos = Boolean(todos && todos.length > 0);

  if (!hasFeed && !hasTodos) return null;

  // The feed defaults open while live, collapsed once settled (the tally
  // strip is enough of a record); the todos below are always visible.
  const feedExpanded = live || (userExpanded ?? false);

  return (
    <div
      className={twMerge(
        "rounded-xl border border-base-200 bg-base-50 px-3 py-2 text-sm",
        className
      )}
      role="status"
      aria-label="Working on it"
      data-testid="agent-activity"
      data-state={live ? "live" : "settled"}
    >
      {hasFeed &&
        (live ? (
          <>
            <WorkingHeader items={items} />
            <FeedRows items={items} />
          </>
        ) : (
          <button
            type="button"
            onClick={() => setUserExpanded((prev) => !(prev ?? false))}
            className="flex w-full items-center gap-3 text-left"
            aria-expanded={feedExpanded}
            data-testid="agent-activity-summary"
          >
            <TallyChips items={items} withLabels />
            <span className="flex items-center gap-0.5 text-xs text-base-400">
              {feedExpanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              details
            </span>
          </button>
        ))}
      {hasFeed && !live && feedExpanded && (
        <div className="mt-1">
          <FeedRows items={items} />
        </div>
      )}
      {hasTodos && (
        <div
          className={twMerge(
            hasFeed && "mt-2 border-t border-base-200 pt-2"
          )}
        >
          <TodoList todos={todos!} delegations={delegations} />
        </div>
      )}
    </div>
  );
}
